import fs from 'fs';
import path from 'path';
import childProcess from 'child_process';
import { UploadFileRaw } from '@/renderer/components/SpacePage/compositions/uploadHandler';
import { transformErrorForSerialization } from '@/common/tools';
import ConstantConfig from '@/renderer/scripts/utils/ConstantConfig';

/**
 * 客户端传参类型
 */
export type FileElectronInfo = {
  path: string; // 文件在本地路径
  name: string;
  size: number;
  mtime: number;
  isDir: boolean; // 是否是目录
  folder?: string; // 根文件夹名
  remotePath?: string; // 文件在网盘路径
  folderSize?: number;
  relativePath?: string; // 上传文件或文件夹所在的上层目录
  raw?: UploadFileRaw;
  // 扫描失败信息
  error?: {
    code: string;
  };
};

export type FolderInfo = {
  path: string;
  name: string;
};

// 判断 Mac 中的替身文件/文件夹
export function isFinderAlias(path: string) {
  let isAlias = false;
  try {
    const contentType = childProcess.execFileSync('mdls', ['-raw', '-name', 'kMDItemContentType', path], {
      encoding: 'utf8',
    });
    isAlias = contentType === 'com.apple.alias-file';
  } catch (error) {}
  return isAlias;
}

// 读取文件
export function fsReadDir(dir: string): Promise<string[]> {
  return new Promise<string[]>((resolve, reject) => {
    fs.readdir(dir, (err, files) => {
      if (err) reject(err);
      resolve(files);
    });
  });
}
// 获取文件信息
export async function fsStat(path: string): Promise<fs.Stats> {
  return new Promise((resolve, reject) => {
    fs.lstat(path, (err, stats) => {
      if (err) return reject(err);
      try {
        if (stats.isSymbolicLink()) return reject(new Error('file stat is null'));
        if (isFinderAlias(path)) return reject({ code: 'NotAllowAliasFile' });
        resolve(stats);
      } catch (e) {
        reject(e);
      }
      resolve(stats);
    });
  });
}

const workingTasks: Record<string, boolean> = {};

/**
 * 转换为Electron中的文件信息
 * @param filePath
 * @param size
 * @param mtime
 * @param isDir
 * @param folder
 * @param folderSize
 * @return FileElectronInfo
 */
export async function convertToElectronInfo(filePath: string, rootFolderName = ''): Promise<FileElectronInfo> {
  const stat = await fsStat(filePath);
  const isDir = stat.isDirectory();
  const info: FileElectronInfo = {
    path: filePath.replace(/\/|\\/g, '/'), // 转换成Mac/windows都能识别的路径
    name: filePath.replace(/^.*(\/|\\)/, ''),
    size: stat.size,
    mtime: stat.mtime.getTime(),
    isDir,
    folder: rootFolderName,
    folderSize: 0,
  };
  return info;
}

export async function listFolder(
  filePath: string,
  options: { taskId?: string; maxFiles?: number } = {},
): Promise<FileElectronInfo[] | { code: string; message: string }> {
  if (options.taskId) workingTasks[options.taskId] = true;
  let folderSize = 0;

  try {
    const rootStat = await fsStat(filePath);
    const rootFolderName = rootStat.isDirectory() ? filePath.replace(/^.*(\/|\\)/, '') : '';
    const queue = [filePath]; // 需要处理的文件路径队列
    const fileList = new Map<string, FileElectronInfo>(); // 保存扫描的文件/文件夹列表
    while (queue.length > 0) {
      const pendingPath = queue.shift();
      if (pendingPath) {
        const item = await convertToElectronInfo(pendingPath, rootFolderName);
        fileList.set(item.path, item);
        // 计算文件数量限制时去掉根文件夹
        if (options.maxFiles && fileList.size - 1 > options.maxFiles) {
          throw new Error('MaxFileLimitReached');
        }

        if (item.isDir) {
          // 获取文件夹下所有文件
          try {
            const nameList = await fsReadDir(pendingPath);
            for (const name of nameList) {
              const subItem = await convertToElectronInfo(path.resolve(pendingPath, name), rootFolderName);
              fileList.set(subItem.path, subItem);
              // 计算文件数量限制时去掉根文件夹
              if (options.maxFiles && fileList.size - 1 > options.maxFiles) {
                throw new Error('MaxFileLimitReached');
              }

              if (subItem.isDir) {
                queue.push(subItem.path);
              } else {
                folderSize += subItem.size;
              }
            }
          } catch (error: MsgError) {
            if (error.message === 'MaxFileLimitReached') {
              throw error;
            }
            // 获取文件夹下文件失败（如文件夹没有权限等），暂时先忽略
            logger.ui.error('[listFolder] fsReadDir', JSON.stringify(error));
            item.error = {
              code: error.code,
            };
          }
        }
      }
    }
    fileList.forEach(item => {
      item.folderSize = folderSize;
      /** 添加上层目录路径，如/Users/A/Downloads/favortest/favor,上传favor目录时，为/Users/A/Downloads/favortest */
      item.relativePath = filePath.replace(/\/|\\/g, '/').split('/').slice(0, -1).join('/');
    });
    return [...fileList.values()];
  } catch (err: MsgError) {
    const error = transformErrorForSerialization(err);
    logger.ui('[listFolder]', error);
    return error;
  }
}
interface ErrorListItem {
  path: string;
  name: string;
  code: string;
  message: string;
}
export async function listFolders(
  filePaths: string[],
  options: { taskId?: string; maxFiles?: number } = {},
): Promise<{ list: FileElectronInfo[]; errorList: ErrorListItem[] }> {
  const starttime = Date.now();
  let list: FileElectronInfo[] = [];
  const errorList: ErrorListItem[] = [];
  for (const filePath of filePaths) {
    const innerStarttime = Date.now();
    const data = await listFolder(filePath, options);
    if (Array.isArray(data)) {
      data.length > ConstantConfig.FILE_COUNT_LIMIT &&
        logger.ui(`[listFolder] scan "${filePath}" folders cost ${(Date.now() - innerStarttime) / 1000}s`);
      list = list.concat(data);
    } else {
      errorList.push({
        path: filePath.replace(/\/|\\/g, '/'), // 转换成Mac/windows都能识别的路径
        name: filePath.replace(/^.*(\/|\\)/, ''),
        code: data.code,
        message: data.message,
      });
    }
  }
  logger.ui(
    `[listFolders] scan "${filePaths.join(', ')}" ${filePaths.length} folders, cost ${
      (Date.now() - starttime) / 1000
    }s`,
  );
  return { list, errorList };
}

export function cancelTask(taskId: string): void {
  if (taskId) delete workingTasks[taskId];
}
